import { useEffect, useRef, useState } from "react";

let sharedObserver: IntersectionObserver | null = null;
const callbacks = new WeakMap<Element, () => void>();

function getObserver() {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          callbacks.get(entry.target)?.();
          callbacks.delete(entry.target);
          sharedObserver?.unobserve(entry.target);
        });
      },
      { rootMargin: "800px 0px" }
    );
  }
  return sharedObserver;
}

/**
 * Defers a CSS background image until its element is close to the viewport,
 * so it doesn't compete with the first screen. One shared observer for all elements.
 */
export function useLazyBackground<T extends HTMLElement>(url: string) {
  const ref = useRef<T>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setLoaded(true);
      return;
    }
    const observer = getObserver();
    callbacks.set(el, () => setLoaded(true));
    observer.observe(el);
    return () => {
      observer.unobserve(el);
      callbacks.delete(el);
    };
  }, []);

  return { ref, backgroundImage: loaded ? `url("${url}")` : undefined };
}
